import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { apiRequest } from '../../utils/api';

const TotpManager = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const resp = await apiRequest('/api/auth/totp/status');
      setEnabled(resp.data.enabled);
    } catch (err) {
      console.error('TOTP status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setError('');
    setActionLoading(true);
    try {
      const resp = await apiRequest('/api/auth/totp/setup', {
        method: 'POST'
      });
      setSetupData(resp.data);
      setDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to setup TOTP');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    setError('');
    setActionLoading(true);
    try {
      await apiRequest('/api/auth/totp/confirm', {
        method: 'POST',
        body: JSON.stringify({ token: confirmCode.trim() })
      });
      setEnabled(true);
      setDialogOpen(false);
      setSetupData(null);
      setConfirmCode('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Invalid code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    setError('');
    setActionLoading(true);
    try {
      await apiRequest('/api/auth/totp/disable', {
        method: 'POST',
        body: JSON.stringify({ password: disablePassword })
      });
      setEnabled(false);
      setDisableDialogOpen(false);
      setDisablePassword('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to disable TOTP');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6">Authenticator App (TOTP)</Typography>
          <CircularProgress size={24} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Authenticator App (TOTP)</Typography>
          <Chip
            label={enabled ? 'Enabled' : 'Not Enabled'}
            color={enabled ? 'success' : 'default'}
            size="small"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use Google Authenticator, Authy, or any TOTP-compatible app to generate verification codes for login.
        </Typography>

        {!enabled ? (
          <Button
            variant="contained"
            onClick={handleSetup}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Set Up Authenticator'}
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="error"
            onClick={() => { setDisableDialogOpen(true); setError(''); }}
            disabled={actionLoading}
          >
            Disable Authenticator
          </Button>
        )}

        {/* Setup Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Set Up Authenticator App</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              1. Open your authenticator app (Google Authenticator, Authy, etc.)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              2. Scan this QR code or enter the secret manually:
            </Typography>
            {setupData && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <QRCodeSVG value={setupData.otpauthUrl} size={200} />
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: 1 }}>
                    {setupData.secret}
                  </Typography>
                  <Tooltip title="Copy secret">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(setupData.secret)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Can't scan? Enter this secret manually in your app.
                </Typography>
              </Box>
            )}
            <Typography variant="body2" sx={{ mb: 2 }}>
              3. Enter the 6-digit code from your app:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              autoFocus
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={actionLoading || confirmCode.length < 6}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Confirm & Enable'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Disable Dialog */}
        <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Disable Authenticator</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter your password to confirm. You will no longer need an authenticator code to log in.
            </Typography>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              autoFocus
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDisable}
              disabled={actionLoading || !disablePassword}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Disable'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default TotpManager;
